import { useController } from 'react-hook-form';
import useAddressQuery, { GoogleAddressFields, parsePlaceComponents } from './useAddressQuery';
import useAddressQueryInputProps from './useAddressQueryInputProps';
import React from 'react';
import styled from 'styled-components';
import { placeDetails } from '../../api/map';
import { ErrorMessage, useScreenQuery } from 'playup-web-core';
import { TextInput } from './ControlledInput';

const addressFields: GoogleAddressFields = [
	'unit_number',
	'street_number',
	'street_name',
	'street_type',
	'city',
	'state',
	'postcode',
];
const ControlledAddressAutoCompleteInput = ({
	countryCode,
	toggleAddressInputModal,
	queryPlaceholder,
	darkMode,
}: {
	countryCode: string;
	toggleAddressInputModal: () => void;
	queryPlaceholder?: string;
	darkMode?: boolean;
}) => {
	const { query, setQuery, predictions, onChangeQuery } = useAddressQuery(countryCode);
	const { queryInputRef, onBlur, onFocus, focused } = useAddressQueryInputProps<HTMLInputElement>();
	const {
		field,
		fieldState: { error },
	} = useController({
		name: 'address',
	});
	const handleSelect = React.useCallback(
		(prediction: { description: string; place_id: string }) => () => {
			setQuery(prediction.description);

			placeDetails(prediction.place_id).then((res) => {
				field.onChange(parsePlaceComponents(res.data.result.address_components, addressFields));
				queryInputRef?.current?.blur();
			});
		},
		[field, queryInputRef, setQuery]
	);
	const { isSmall } = useScreenQuery();
	return (
		<Container darkMode={darkMode}>
			<TextInput
				darkMode={darkMode}
				placeholder={queryPlaceholder || 'Start typing your address'}
				error={error}
				isSmallScreen={isSmall}
				onFocus={onFocus}
				onBlur={onBlur}
				ref={queryInputRef}
				value={query}
				onChange={(e) => {
					onChangeQuery(e.target.value);
				}}
			/>

			{focused && (
				<PredictionsContainer isSmall={isSmall}>
					{predictions?.map((prediction, idx) => {
						return (
							<Predictions
								isSmall={isSmall}
								style={{
									borderBottomColor: idx === predictions.length - 1 ? 'black' : 'gray',
								}}
								key={prediction.place_id}
								onClick={handleSelect(prediction)}
							>
								<PredictionsText isSmall={isSmall}>{prediction.description}</PredictionsText>
							</Predictions>
						);
					})}
					<Predictions isSmall={isSmall} onClick={toggleAddressInputModal}>
						<ToggleAddressButtonText isSmall={isSmall}>Enter Address Manually</ToggleAddressButtonText>
					</Predictions>
				</PredictionsContainer>
			)}
			{error &&
				Object.keys(error).map((item) => {
					return error?.[item]?.message && <ErrorMessage textAlign="left">{error?.[item]?.message}</ErrorMessage>;
				})}
		</Container>
	);
};
const Container = styled.div<{ darkMode?: boolean }>(({ darkMode }) => ({
	flex: 1,
	backgroundColor: darkMode ? '#2D3134' : 'white',
}));

const PredictionsContainer = styled.div<{ isSmall: boolean }>(({ isSmall }) => ({
	maxHeight: isSmall ? 280 : 400,
	backgroundColor: 'white',
	opacity: 1,
	overflowY: 'scroll',
}));
const Predictions = styled.div<{ isSmall: boolean }>(({ isSmall }) => ({
	paddingTop: isSmall ? 12 : 14,
	paddingBottom: isSmall ? 12 : 14,
	paddingLeft: 11,
	borderColor: '#DDE1E3',
	borderBottomWidth: 1,
	borderLeftWidth: 1,
	borderRightWidth: 1,
	cursor: 'pointer',
	display: 'flex',
}));
const PredictionsText = styled.p<{ isSmall: boolean }>(({ isSmall }) => ({
	color: '#A4A6A8',
	fontWeight: '600',
	fontSize: isSmall ? 14 : 18,
}));
const ToggleAddressButtonText = styled.p<{ isSmall: boolean }>(({ isSmall }) => ({
	color: '#0081F3',
	fontWeight: '600',
	fontSize: isSmall ? 14 : 18,
}));

export default ControlledAddressAutoCompleteInput;
